import * as policySupport from "./support";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Checkbox, Link, Paper, Stack, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Component from "components/Component";
import BigLoader from "components/common/BigLoader";
import Disabled from "components/common/Disabled";
import ExtraInput from "components/common/ExtraInput";
import * as jsConfig from "config";
import * as labels from "config.labels";
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as endorsementStore from "store/endorsements";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const isUnknownEndorsement = ({ config, endorsement }) => {
  const { title } = endorsement ?? {};
  const endorsementConfig = config?.items?.[title] ?? {};
  const allEndorsements = config?.defaultVisibleEndorsements ?? [];
  return (
    (!endorsementConfig.hasTemplate && !endorsementConfig.isTemplateOptional) ||
    !allEndorsements.includes(title)
  );
};

const LinkButton = ({ onClick, label }) => (
  <Disabled ifReadOnly>
    <Link
      component={"button"}
      variant={"body2"}
      color={"secondary"}
      onClick={onClick}
      sx={{ display: "block", marginBottom: "8px" }}
    >
      {label}
    </Link>
  </Disabled>
);

const EndorsementEntry = ({ title, config, endorsements, setEndorsements }) => {
  const selectEndorsement = () => {
    const newEndorsements = [
      ...(endorsements ?? []).filter((e) => e.title !== title),
      policySupport.makeEndorsement(config)(title),
    ];

    const allEndorsements = config?.defaultVisibleEndorsements ?? [];
    const titleToIndexMapping = Object.fromEntries(
      allEndorsements.map((title, i) => [title, i])
    );
    newEndorsements.sort(
      (a, b) =>
        (titleToIndexMapping[a.title] ?? allEndorsements.length) -
        (titleToIndexMapping[b.title] ?? allEndorsements.length)
    );
    setEndorsements(newEndorsements);
  };

  const deselectEndorsement = () =>
    setEndorsements((endorsements ?? []).filter((e) => e.title !== title));

  const isSelected = endorsements?.some((e) => e.title === title);

  const endorsement = isSelected
    ? endorsements?.find((e) => e.title === title)
    : policySupport.makeEndorsement(config)(title);

  const extraInputs = config?.items?.[title]?.extraInputs;
  const updateExtraInput = (key, value) =>
    setEndorsements(
      endorsements?.map((e) =>
        e.title === title
          ? {
              ...e,
              [key]: value,
            }
          : e
      )
    );

  return (
    <Stack direction={"column"} spacing={1}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-start"}
        spacing={1}
        onClick={isSelected ? deselectEndorsement : selectEndorsement}
        sx={{ cursor: "pointer" }}
      >
        <Disabled ifReadOnly>
          <Checkbox checked={isSelected} />
        </Disabled>
        <Typography variant={"body"} sx={{ width: "100%" }}>
          {policySupport.makeEndorsementLabel({ config, endorsement })}
        </Typography>
      </Stack>
      {isSelected && extraInputs && (
        <Stack direction={"column"} sx={{ paddingLeft: 10 }}>
          {extraInputs?.map((extraInput) => (
            <ExtraInput
              key={extraInput.key}
              definition={extraInput}
              value={endorsement?.[extraInput.key]}
              setValue={(newValue) =>
                updateExtraInput(extraInput.key, newValue)
              }
            />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const Endorsements = ({ config, endorsements, setEndorsements }) => {
  return (
    <Stack direction={"column"} spacing={1}>
      {(config?.defaultVisibleEndorsements ?? [])
        .filter(
          (title) => !isUnknownEndorsement({ config, endorsement: { title } })
        )
        .map((title) => (
          <EndorsementEntry
            key={title}
            title={title}
            config={config}
            endorsements={endorsements}
            setEndorsements={setEndorsements}
          />
        ))}
    </Stack>
  );
};

const UnknownEndorsements = ({ config, endorsements, setEndorsements }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const remove = (endorsement) => {
    setEndorsements(endorsements.filter((e) => e.title !== endorsement?.title));
  };
  const unknownTitle = translateLabels(labels.UNKNOWN_ENDORSEMENTS_TITLE, {
    default: "Unknown Endorsements",
  });
  const unknownDescription = (
    <>
      {translateLabels(labels.UNKNOWN_ENDORSEMENTS_DESCRIPTION, {
        default:
          "The following selected endorsements are not available, please contact: ",
      })}
      <Link
        href={`mailto:${jsConfig.SUPPORT_EMAIL}?subject=${encodeURIComponent(
          unknownTitle
        )}`}
      >
        {jsConfig.SUPPORT_EMAIL}
      </Link>
      {"."}
    </>
  );

  const unknownEndorsements = (endorsements ?? []).filter((endorsement) =>
    isUnknownEndorsement({ config, endorsement })
  );

  if (!unknownEndorsements.length) {
    return <></>;
  }
  return (
    <Paper sx={{ width: "100%" }}>
      <Stack
        direction={"column"}
        spacing={2}
        sx={{
          padding: 2,
          background: "mistyrose",
        }}
      >
        <Stack direction={"row"} justifyContent={"space-between"}>
          <Stack direction={"column"} spacing={1}>
            <Typography variant={"subtitle1"}>{unknownTitle}</Typography>
            <Typography variant={"subtitle2"} color={"secondary"}>
              {unknownDescription}
            </Typography>
          </Stack>
        </Stack>
        <Stack direction={"column"} spacing={1}>
          {unknownEndorsements.map((endorsement) => (
            <Stack direction={"row"} spacing={2} key={endorsement.title}>
              <Disabled ifReadOnly>
                <IconButton onClick={() => remove(endorsement)}>
                  <DeleteIcon color={"primary"} size={"large"} />
                </IconButton>
              </Disabled>
              <Typography variant={"body"} sx={{ paddingTop: "7px" }}>
                {policySupport.makeEndorsementLabel({ config, endorsement })}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Stack>
    </Paper>
  );
};

export const FixedListEndorsements = ({ layerId }) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");

  const rawEndorsements = useSelector(endorsementStore.select)
    ?.endorsementsByLayer?.[layerId];
  const endorsements = rawEndorsements ?? [];

  const { config, isLoading } = policySupport.usePolicyConfig({ layerId });

  const dispatch = useDispatch();

  const setEndorsements = useCallback(
    (newEndorsements) =>
      dispatch(
        endorsementStore.setLayerEndorsements({
          layerId,
          endorsements: newEndorsements,
        })
      ),
    [dispatch, layerId]
  );

  useEffect(() => {
    /* Set the default selected endorsements on the layer if none are set. */
    if (
      rawEndorsements == null &&
      config?.defaultSelectedEndorsements?.length
    ) {
      setEndorsements(policySupport.makeDefaultEndorsements(config));
    }
  }, [rawEndorsements, config, setEndorsements]);

  return (
    <Component
      title={translateLabels(labels.ENDORSEMENTS, {
        default: "Endorsements",
      })}
      options={
        <Stack direction={"row"} spacing={config.GRID_SPACING} padding={0}>
          {config?.defaultSelectedEndorsements != null && (
            <LinkButton
              onClick={() =>
                setEndorsements(policySupport.makeDefaultEndorsements(config))
              }
              label={translateLabels(labels.ENDORSEMENTS_RESET_TO_DEFAULT, {
                default: "Reset to defaults",
              })}
            />
          )}
        </Stack>
      }
    >
      {isLoading ? (
        <BigLoader />
      ) : (
        <Stack direction={"column"} spacing={2}>
          <UnknownEndorsements
            config={config}
            endorsements={endorsements}
            setEndorsements={setEndorsements}
          />
          <Endorsements
            config={config}
            endorsements={endorsements}
            setEndorsements={setEndorsements}
          />
        </Stack>
      )}
    </Component>
  );
};

export default FixedListEndorsements;
